import { stampPdfFooter, activeUnitAddress } from './reportProfiles';
import jsPDF from 'jspdf';
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType,
  Table, TableRow, TableCell, WidthType, BorderStyle, Footer, PageNumber,
} from 'docx';
import { commercialConfigAPI } from '../services/api';

// Document-type presets: label shown on the letterhead, doc-ref prefix, and
// whether a formal 3-role approval table is warranted (protocol/report are
// controlled-document drafts; advisory notes just need a single review line).
const DOC_TYPE_PRESETS = {
  protocol: { label: 'Cleaning Validation Protocol (AI Draft)', prefix: 'PROT', formalSignoff: true },
  report: { label: 'Report (AI Draft)', prefix: 'REP', formalSignoff: true },
  note: { label: 'AI Advisory Note', prefix: 'NOTE', formalSignoff: false },
};

const SIGNOFF_ROLES = ['Prepared By', 'Reviewed By', 'Approved By'];

let cachedProfile = null;
let cachedProfilePromise = null;

// Company profile (name/address) is set once in Settings and reused for every
// exported document's letterhead — fetched once per session and cached. The
// selected unit's own address, when it has one, replaces the company address.
const withUnitAddress = (profile) => ({ ...profile, company_address: activeUnitAddress() || profile?.company_address || '' });

export const getCompanyProfile = (appName = 'PrimeClean') => {
  if (cachedProfile) return Promise.resolve(withUnitAddress(cachedProfile));
  if (!cachedProfilePromise) {
    cachedProfilePromise = commercialConfigAPI.getRuntime()
      .then((res) => {
        cachedProfile = res?.data?.data?.profile || {};
        return cachedProfile;
      })
      .catch(() => ({ company_name: appName, company_address: '' }));
  }
  return cachedProfilePromise.then(withUnitAddress);
};

const draftRef = (prefix) => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `AI-${prefix}-${stamp}`;
};

// Parses the AI's markdown-ish plain text into typed blocks the PDF/DOCX
// builders can both consume, so heading/bullet/numbered-list detection only
// lives in one place.
const parseBlocks = (content) => {
  const lines = String(content || '').split('\n');
  const blocks = [];
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line) { blocks.push({ type: 'blank' }); continue; }
    if (/^#{1,3}\s/.test(line)) {
      const level = line.match(/^(#{1,3})\s/)[1].length;
      blocks.push({ type: `h${level}`, text: line.replace(/^#{1,3}\s/, '') });
    } else if (/^[-*]\s/.test(line)) {
      blocks.push({ type: 'bullet', text: line.replace(/^[-*]\s/, '') });
    } else if (/^\d+[.)]\s/.test(line)) {
      blocks.push({ type: 'number', text: line.replace(/^\d+[.)]\s/, '') });
    } else {
      blocks.push({ type: 'para', text: line });
    }
  }
  return blocks;
};

// ---------------------------------------------------------------- PDF ----

export const exportAIDocToPdf = async (title, content, fileName, docType = 'note', appName = 'PrimeClean') => {
  const preset = DOC_TYPE_PRESETS[docType] || DOC_TYPE_PRESETS.note;
  const profile = await getCompanyProfile(appName);
  const ref = draftRef(preset.prefix);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const marginX = 48;
  let y = 50;
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const maxWidth = pageWidth - marginX * 2;
  const bottomLimit = pageHeight - 56;

  const ensureSpace = (h) => {
    if (y + h > bottomLimit) {
      doc.addPage();
      y = 50;
    }
  };

  // Letterhead
  doc.setFontSize(15);
  doc.setFont(undefined, 'bold');
  doc.text(profile.company_name || appName, marginX, y);
  y += 16;
  if (profile.company_address) {
    doc.setFontSize(8.5);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100);
    const addrLines = doc.splitTextToSize(profile.company_address, maxWidth);
    for (const l of addrLines) { doc.text(l, marginX, y); y += 11; }
    doc.setTextColor(0);
  }
  y += 4;
  doc.setDrawColor(180);
  doc.line(marginX, y, pageWidth - marginX, y);
  y += 18;

  // Document control block
  doc.setFontSize(8.5);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100);
  doc.text(`Document Type: ${preset.label}`, marginX, y);
  doc.text(`Draft Ref: ${ref}`, pageWidth - marginX, y, { align: 'right' });
  y += 12;
  doc.text(`Generated: ${new Date().toLocaleString()}`, marginX, y);
  doc.text('Status: DRAFT — Pending QA Review', pageWidth - marginX, y, { align: 'right' });
  doc.setTextColor(0);
  y += 22;

  // Title
  doc.setFontSize(13);
  doc.setFont(undefined, 'bold');
  const titleLines = doc.splitTextToSize(title, maxWidth);
  for (const l of titleLines) { doc.text(l, marginX, y); y += 17; }
  y += 8;

  // Body
  for (const block of parseBlocks(content)) {
    if (block.type === 'blank') { y += 8; continue; }

    let fontSize = 10;
    let fontStyle = 'normal';
    let indent = 0;
    let text = block.text;

    if (block.type === 'h1') { fontSize = 12.5; fontStyle = 'bold'; y += 8; }
    else if (block.type === 'h2') { fontSize = 11.5; fontStyle = 'bold'; y += 6; }
    else if (block.type === 'h3') { fontSize = 10.5; fontStyle = 'bold'; y += 4; }
    else if (block.type === 'bullet') { text = `•  ${text}`; indent = 10; }
    else if (block.type === 'number') { indent = 10; }

    doc.setFontSize(fontSize);
    doc.setFont(undefined, fontStyle);
    const wrapped = doc.splitTextToSize(text, maxWidth - indent);
    for (const w of wrapped) {
      ensureSpace(14);
      doc.text(w, marginX + indent, y);
      y += 14;
    }
  }

  // Signature block
  if (preset.formalSignoff) {
    ensureSpace(90);
    y += 16;
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Approval', marginX, y);
    y += 10;

    const colWidths = [maxWidth * 0.22, maxWidth * 0.34, maxWidth * 0.22, maxWidth * 0.22];
    const rowHeight = 34;
    const headers = ['Role', 'Name / Designation', 'Signature', 'Date'];

    doc.setFontSize(8.5);
    doc.setDrawColor(150);
    let x = marginX;
    doc.setFont(undefined, 'bold');
    for (let i = 0; i < headers.length; i++) {
      doc.rect(x, y, colWidths[i], 16);
      doc.text(headers[i], x + 4, y + 11);
      x += colWidths[i];
    }
    y += 16;

    doc.setFont(undefined, 'normal');
    for (const role of SIGNOFF_ROLES) {
      ensureSpace(rowHeight);
      x = marginX;
      for (let i = 0; i < colWidths.length; i++) {
        doc.rect(x, y, colWidths[i], rowHeight);
        if (i === 0) doc.text(role, x + 4, y + rowHeight / 2 + 3);
        x += colWidths[i];
      }
      y += rowHeight;
    }
  } else {
    ensureSpace(30);
    y += 14;
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text('Reviewed by: _______________________     Date: ______________', marginX, y);
  }

  // Footer with page numbers on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(7.5);
    doc.setTextColor(140);
    doc.text('AI-assisted draft — advisory only, requires QA review and formal approval before use as a controlled record.', marginX, pageHeight - 30);
    doc.text(`Page ${p} of ${pageCount}`, pageWidth - marginX, pageHeight - 30, { align: 'right' });
    doc.setTextColor(0);
  }

  stampPdfFooter(doc, 'ai_document', { pageNumbers: false });
  doc.save(fileName || `${(title || 'document').replace(/[^a-z0-9]+/gi, '_')}.pdf`);
};

// Back-compat alias (old simple exporter, kept in case other callers still import it directly).
export const exportAITextToPdf = (title, content, fileName, appName = 'PrimeClean') => exportAIDocToPdf(title, content, fileName, 'note', appName);

// --------------------------------------------------------------- DOCX ----

const docxHeadingRun = (text, level) => new Paragraph({ heading: level, spacing: { before: 200, after: 100 }, children: [new TextRun({ text, bold: true })] });

const blocksToDocxParagraphs = (content) => {
  const paragraphs = [];
  for (const block of parseBlocks(content)) {
    if (block.type === 'blank') { paragraphs.push(new Paragraph({ text: '' })); continue; }
    if (block.type === 'h1') { paragraphs.push(docxHeadingRun(block.text, HeadingLevel.HEADING_1)); continue; }
    if (block.type === 'h2') { paragraphs.push(docxHeadingRun(block.text, HeadingLevel.HEADING_2)); continue; }
    if (block.type === 'h3') { paragraphs.push(docxHeadingRun(block.text, HeadingLevel.HEADING_3)); continue; }
    if (block.type === 'bullet') { paragraphs.push(new Paragraph({ text: block.text, bullet: { level: 0 } })); continue; }
    if (block.type === 'number') { paragraphs.push(new Paragraph({ text: block.text, numbering: { reference: 'ai-doc-numbering', level: 0 } })); continue; }
    paragraphs.push(new Paragraph({ text: block.text, spacing: { after: 80 } }));
  }
  return paragraphs;
};

const cell = (text, opts = {}) => new TableCell({
  width: { size: opts.width || 25, type: WidthType.PERCENTAGE },
  children: [new Paragraph({ text: text || '', bold: !!opts.bold })],
  margins: { top: 60, bottom: 60, left: 80, right: 80 },
});

const thinBorder = { style: BorderStyle.SINGLE, size: 2, color: 'AAAAAA' };
const tableBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder, insideHorizontal: thinBorder, insideVertical: thinBorder };

const buildSignoffTable = () => new Table({
  width: { size: 100, type: WidthType.PERCENTAGE },
  borders: tableBorders,
  rows: [
    new TableRow({ children: [cell('Role', { bold: true, width: 20 }), cell('Name / Designation', { bold: true, width: 35 }), cell('Signature', { bold: true, width: 25 }), cell('Date', { bold: true, width: 20 })] }),
    ...SIGNOFF_ROLES.map((role) => new TableRow({ children: [cell(role, { width: 20 }), cell('', { width: 35 }), cell('', { width: 25 }), cell('', { width: 20 })] })),
  ],
});

export const exportAIDocToWord = async (title, content, fileName, docType = 'note', appName = 'PrimeClean') => {
  const preset = DOC_TYPE_PRESETS[docType] || DOC_TYPE_PRESETS.note;
  const profile = await getCompanyProfile(appName);
  const ref = draftRef(preset.prefix);

  const children = [
    new Paragraph({ children: [new TextRun({ text: profile.company_name || appName, bold: true, size: 30 })] }),
    ...(profile.company_address ? [new Paragraph({ children: [new TextRun({ text: profile.company_address, size: 18, color: '666666' })] })] : []),
    new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'AAAAAA' } }, spacing: { after: 200 } }),
    new Paragraph({
      spacing: { after: 100 },
      children: [
        new TextRun({ text: `Document Type: ${preset.label}    `, size: 16, color: '666666' }),
        new TextRun({ text: `Draft Ref: ${ref}`, size: 16, color: '666666' }),
      ],
    }),
    new Paragraph({
      spacing: { after: 300 },
      children: [
        new TextRun({ text: `Generated: ${new Date().toLocaleString()}    `, size: 16, color: '666666' }),
        new TextRun({ text: 'Status: DRAFT — Pending QA Review', size: 16, color: '666666' }),
      ],
    }),
    new Paragraph({ heading: HeadingLevel.TITLE, spacing: { after: 200 }, children: [new TextRun({ text: title, bold: true })] }),
    ...blocksToDocxParagraphs(content),
  ];

  if (preset.formalSignoff) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 120 }, children: [new TextRun({ text: 'Approval', bold: true })] }));
    children.push(buildSignoffTable());
  } else {
    children.push(new Paragraph({ spacing: { before: 300 }, text: 'Reviewed by: _______________________     Date: ______________' }));
  }

  const docxDocument = new Document({
    numbering: { config: [{ reference: 'ai-doc-numbering', levels: [{ level: 0, format: 'decimal', text: '%1.', alignment: AlignmentType.START }] }] },
    sections: [{
      properties: {},
      footers: {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({ text: 'AI-assisted draft — advisory only, requires QA review and formal approval before use as a controlled record.  ·  Page ', size: 14, color: '999999' }),
              new TextRun({ children: [PageNumber.CURRENT], size: 14, color: '999999' }),
              new TextRun({ text: ' of ', size: 14, color: '999999' }),
              new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 14, color: '999999' }),
            ],
          })],
        }),
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(docxDocument);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || `${(title || 'document').replace(/[^a-z0-9]+/gi, '_')}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
